import api from "@/api/api";
import { toast } from "@/components/utils/ui_v4/alerts/toast/toast";
import { computed, nextTick, ref, watch } from "vue";
import { formatCNPJ } from "../utils/stringUtils";

const MSG_APENAS_UM_REPRESENTANTE =
  "Não é permitido ter mais de um representante selecionado para participar desta cotação. Escolha apenas um entre os contatos.";

export default function useRevisarContatosDialog(props, emit) {
  const dialogVisible = ref(false);
  const currentIndex = ref(0);
  const mostrarSomenteSemContato = ref(false);
  const editarContatoVisivel = ref(false);
  const contatoEditando = ref(null);
  const novoContatoVisivel = ref(false);
  const contatosSelecionados = ref(new Set());
  const fornecedoresLocal = ref([]);
  const carregandoRepresentantes = ref(false);

  // Sincronizar fornecedores locais com props
  watch(
    () => props.fornecedores,
    (newFornecedores) => {
      if (newFornecedores && Array.isArray(newFornecedores)) {
        fornecedoresLocal.value = newFornecedores.map(f => ({ ...f }));
      } else {
        fornecedoresLocal.value = [];
      }
    },
    { immediate: true, deep: true }
  );

  const fornecedoresFiltrados = computed(() => {
    if (!fornecedoresLocal.value || fornecedoresLocal.value.length === 0) {
      return [];
    }
    if (!mostrarSomenteSemContato.value) {
      return fornecedoresLocal.value;
    }
    return fornecedoresLocal.value.filter(
      (fornecedor) => !fornecedor.representantes || fornecedor.representantes.length === 0
    );
  });

  const fornecedorAtual = computed(() => fornecedoresFiltrados.value[currentIndex.value] || null);

  const sincronizarIndice = () => {
    const maxIndex = Math.max(fornecedoresFiltrados.value.length - 1, 0);
    currentIndex.value = Math.min(Math.max(props.initialIndex || 0, 0), maxIndex);
  };

  watch(
    () => props.visible,
    (newVal) => {
      dialogVisible.value = newVal;
      if (newVal) {
        sincronizarIndice();
      }
    },
    { immediate: true }
  );

  watch(
    () => props.initialIndex,
    () => {
      if (dialogVisible.value) {
        sincronizarIndice();
      }
    }
  );

  watch(
    () => props.fornecedores,
    () => {
      if (dialogVisible.value) {
        sincronizarIndice();
      }
    }
  );

  watch(dialogVisible, async (newVal, oldVal) => {
    if (!newVal && oldVal && props.handleClose) {
      props.handleClose();
    }
    if (!newVal) {
      editarContatoVisivel.value = false;
      contatoEditando.value = null;
      novoContatoVisivel.value = false;
      // Limpar seleção só após o modal estar fechado (transição), para os checks permanecerem visíveis ao salvar/fechar
      setTimeout(() => {
        contatosSelecionados.value.clear();
      }, 400);
    } else if (newVal) {
      await nextTick();
      await carregarRepresentantesFornecedor();
    }
  });

  watch(fornecedorAtual, async (novoFornecedor, fornecedorAnterior) => {
    if (novoFornecedor && fornecedorAnterior) {
      const novoId = novoFornecedor.idfornecedor || novoFornecedor.id;
      const anteriorId = fornecedorAnterior.idfornecedor || fornecedorAnterior.id;
      if (novoId === anteriorId) {
        await nextTick();
        inicializarSelecoes();
        return;
      }
    }
    await nextTick();
    await carregarRepresentantesFornecedor();
  });

  const carregarRepresentantesFornecedor = async () => {
    if (!props.idCotacao || !fornecedorAtual.value) {
      return;
    }

    if (carregandoRepresentantes.value) {
      return;
    }

    const fornecedorId = fornecedorAtual.value.idfornecedor || fornecedorAtual.value.id;
    if (!fornecedorId) {
      return;
    }

    const fornecedorIndex = fornecedoresLocal.value.findIndex(
      f => (f.idfornecedor || f.id) === fornecedorId
    );

    if (fornecedorIndex !== -1) {
      const fornecedor = fornecedoresLocal.value[fornecedorIndex];
      if (fornecedor.representantes && fornecedor.representantes.length > 0) {
        const temParticipa = fornecedor.representantes.some(rep => rep.hasOwnProperty('participa'));
        if (temParticipa) {
          await nextTick();
          inicializarSelecoes();
          return;
        }
      }
    }

    carregandoRepresentantes.value = true;

    try {
      const { data: response } = await api.get(
        `/cotacao/listar_representantes_fornecedor/${props.idCotacao}/${fornecedorId}`
      );

      if (response && response.success && response.representantes) {
        if (fornecedorIndex !== -1) {
          const novosFornecedores = [...fornecedoresLocal.value];
          novosFornecedores[fornecedorIndex] = {
            ...novosFornecedores[fornecedorIndex],
            representantes: response.representantes
          };
          fornecedoresLocal.value = novosFornecedores;
        }

        await nextTick();
        inicializarSelecoes();
      }
    } catch (error) {

      inicializarSelecoes();
    } finally {
      carregandoRepresentantes.value = false;
    }
  };

  const inicializarSelecoes = () => {
    const newSet = new Set();
    const fornecedor = fornecedorAtual.value;

    if (fornecedor && Array.isArray(fornecedor.representantes)) {
      const participando = fornecedor.representantes.filter((contato) => {
        const participa = contato.participa;
        return participa === true || participa === 'true' || participa === 1;
      });
      // Apenas um representante por fornecedor na cotação
      if (participando.length > 0) {
        const primeiro = participando[0];
        const key = getContatoKey(primeiro);
        if (key) {
          newSet.add(key);
        }
      }
    }

    contatosSelecionados.value = newSet;
  };

  watch(fornecedoresFiltrados, (lista) => {
    if (lista.length === 0) {
      currentIndex.value = 0;
    } else if (currentIndex.value >= lista.length) {
      currentIndex.value = lista.length - 1;
    }
  });

  const formatTelefone = (telefone) => {
    if (!telefone) return '—';
    const digits = telefone.toString().replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const contatos = computed(() => {
    if (fornecedorAtual.value && Array.isArray(fornecedorAtual.value.representantes)) {
      const contatosFormatados = fornecedorAtual.value.representantes.map((contato) => ({
        ...contato,
        telefone_formatado: formatTelefone(contato.telefone || contato.whatsapp),
      }));

      return [...contatosFormatados].sort((a, b) => {
        const aSelecionado = isContatoSelecionado(a);
        const bSelecionado = isContatoSelecionado(b);
        if (aSelecionado && !bSelecionado) return -1;
        if (!aSelecionado && bSelecionado) return 1;
        return 0;
      });
    }
    return [];
  });

  const fornecedoresCount = computed(() => fornecedoresFiltrados.value.length);

  const contatosSelecionadosCount = computed(() => {
    if (!fornecedorAtual.value || contatos.value.length === 0) return 0;
    void contatosSelecionados.value.size;
    return contatos.value.filter(contato => isContatoSelecionado(contato)).length;
  });

  const getContatoKey = (contato) => {
    return contato.cotacao_representante_fornecedor_id || `${contato.email}_${contato.telefone}`;
  };

  const isContatoSelecionado = (contato) => {
    const key = getContatoKey(contato);
    return contatosSelecionados.value.has(key);
  };

  const selecionarContato = (contato) => {
    const key = getContatoKey(contato);
    if (!key) return;
    contatosSelecionados.value = new Set([key]);
  };

  const desselecionarContato = (contato) => {
    const key = getContatoKey(contato);
    const newSet = new Set(contatosSelecionados.value);
    newSet.delete(key);
    contatosSelecionados.value = newSet;
  };

  const toggleContato = (contato) => {
    if (isContatoSelecionado(contato)) {
      desselecionarContato(contato);
      return;
    }
    const tinhaOutro = contatos.value.some((c) => isContatoSelecionado(c));
    if (tinhaOutro) {
      toast.warning(MSG_APENAS_UM_REPRESENTANTE);
    }
    selecionarContato(contato);
  };

  const hasPrev = computed(() => currentIndex.value > 0);
  const hasNext = computed(() => currentIndex.value < fornecedoresCount.value - 1);

  const prevFornecedor = () => {
    if (hasPrev.value) {
      currentIndex.value -= 1;
    }
  };

  const nextFornecedor = () => {
    if (hasNext.value) {
      currentIndex.value += 1;
    }
  };


  const emitAdicionarContato = () => {
    if (!fornecedorAtual.value) return;
    novoContatoVisivel.value = true;
  };

  const handleEditarContato = (contato) => {
    contatoEditando.value = { ...contato };
    editarContatoVisivel.value = true;
  };

  const handleFecharEditarContato = () => {
    editarContatoVisivel.value = false;
    contatoEditando.value = null;
  };

  const handleSalvarContatoEditado = (contatoAtualizado, callback) => {
    emit('salvar-contato', {
      fornecedor: fornecedorAtual.value,
      contato: {
        ...contatoEditando.value,
        ...contatoAtualizado,
      },
    }, (resultado) => {
      if (callback) callback(resultado);
      if (resultado && resultado.success) {
        editarContatoVisivel.value = false;
      }
    });
  };

  const handleExcluirContato = (contato) => {
    emit('excluir-contato', {
      fornecedor: fornecedorAtual.value,
      contato,
    });
  };

  const handleSalvar = async () => {
    if (!props.idCotacao) {
      toast.error("ID da cotação não informado.");
      return;
    }

    if (!fornecedorAtual.value) {
      toast.error("Fornecedor não encontrado.");
      return;
    }

    const fornecedorId = fornecedorAtual.value.idfornecedor || fornecedorAtual.value.id;
    if (!fornecedorId) {
      toast.error("ID do fornecedor não encontrado.");
      return;
    }

    const contatosSelecionadosList = contatos.value.filter(contato => isContatoSelecionado(contato));
    if (contatosSelecionadosList.length > 1) {
      toast.error(MSG_APENAS_UM_REPRESENTANTE);
      return;
    }
    const representantesSelecionados = contatosSelecionadosList.map(
      contato => contato.cotacao_representante_fornecedor_id
    ).filter(id => id != null);

    try {
      const payload = {
        fornecedores: [
          {
            idfornecedor: fornecedorId,
            representantes_selecionados: Array.isArray(representantesSelecionados) ? representantesSelecionados : []
          }
        ]
      };

      const { data: response } = await api.post(
        `/cotacao/salvar_participacoes_representantes/${props.idCotacao}`,
        payload
      );

      if (response && response.success) {
        if (representantesSelecionados.length === 0) {
          toast.success("Todas as participações foram removidas com sucesso!");
        } else {
          toast.success("Participações salvas com sucesso!");
        }

        const keysAntesRefetch = new Set(contatosSelecionados.value);

        await carregarRepresentantesFornecedor();

        if (keysAntesRefetch.size > 0) {
          const setAtual = new Set(contatosSelecionados.value);
          keysAntesRefetch.forEach(key => setAtual.add(key));
          contatosSelecionados.value = setAtual;
        }

        emit('salvar', {
          fornecedor: fornecedorAtual.value,
          contatosSelecionados: contatosSelecionadosList,
          nenhumContatoSelecionado: representantesSelecionados.length === 0
        });
      } else {
        const errorMsg = (response && response.message) || "Erro ao salvar participações.";
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorResponse = error && error.response;
      const errorData = errorResponse && errorResponse.data;
      const errorMessage = (errorData && errorData.message) || (error && error.message) || "Erro ao salvar participações.";
      toast.error(errorMessage);

    }
  };

  const handleSalvarNovoContato = (payload, callback) => {
    emit('criar-contato', payload, async (resultado) => {
      if (callback) callback(resultado);
      if (resultado && resultado.success) {
        novoContatoVisivel.value = false;
        if (resultado.contato) {
          await nextTick();
          await nextTick();

          if (fornecedorAtual.value && Array.isArray(fornecedorAtual.value.representantes)) {
            const userId = resultado.contato.cotacao_representante_fornecedor_id;
            const userEmail = resultado.contato.email;

            const contatoNaLista = fornecedorAtual.value.representantes.find(c => {
              if (userId && c.cotacao_representante_fornecedor_id === userId) return true;
              if (!userId && c.email === userEmail) return true;
              return false;
            });

            if (contatoNaLista) {
              contatoNaLista.participa = true;
              selecionarContato(contatoNaLista);
              await nextTick();
              await handleSalvar();
              return;
            }
          }

          // Fallback se não encontrar na lista (improvável se o refresh funcionou)
          selecionarContato(resultado.contato);
          await nextTick();
          await handleSalvar();
        }
      }
    });
  };

  const handleFecharNovoContato = () => {
    novoContatoVisivel.value = false;
  };

  const handleEditarContatoDuplicado = (contato) => {
    handleEditarContato(contato);
  };

  const closeDialog = () => {
    dialogVisible.value = false;
  };

  return {
    dialogVisible,
    currentIndex,
    mostrarSomenteSemContato,
    editarContatoVisivel,
    contatoEditando,
    novoContatoVisivel,
    fornecedoresFiltrados,
    fornecedorAtual,
    contatos,
    contatosSelecionadosCount,
    hasPrev,
    hasNext,
    prevFornecedor,
    nextFornecedor,
    formatCNPJ,
    isContatoSelecionado,
    toggleContato,
    emitAdicionarContato,
    handleEditarContato,
    handleFecharEditarContato,
    handleSalvarContatoEditado,
    handleExcluirContato,
    handleSalvar,
    handleSalvarNovoContato,
    handleFecharNovoContato,
    handleEditarContatoDuplicado,
    closeDialog,
  };
}
